import * as React from 'react';
import { useMutation } from 'react-query';
import { Outlier, OutlierRow } from './OutlierRow';
import MultiplePhotographersInvoiceApi from '../MultiplePhotographersInvoiceApi';
import { Translate } from '../../../../../shared/LanguageService';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

type OutliersSectionProps = {
    outliers: Outlier[];
};

function reviewedOutliers(outlier: Outlier): boolean {
    return !outlier.hasBeenReviewed;
}

function optimisticallyRemovedOutliers(
    optimisticallyRemoved: number[]
): (outlier: Outlier) => boolean {
    return function filter(outlier: Outlier): boolean {
        // @ts-ignore
        return !optimisticallyRemoved.includes(outlier.id);
    };
}

export function OutliersSection({ outliers }: OutliersSectionProps) {
    let markAsReviewedMutation = useMutation<void, unknown, { outlierId: number }>(
        ({ outlierId }: { outlierId: number }) =>
            MultiplePhotographersInvoiceApi.markOutlierAsReviewed(outlierId)
    );
    let [optimisticallyRemoved, setOptimisticallyRemoved] = React.useState<number[]>([]);

    function handleMarkAsReviewed(outlierId: number): Promise<void> {
        setOptimisticallyRemoved((prev) => prev.concat([outlierId]));

        return markAsReviewedMutation.mutateAsync({ outlierId });
    }

    let displayedOutliers = outliers
        .filter(reviewedOutliers)
        .filter(optimisticallyRemovedOutliers(optimisticallyRemoved));

    if (displayedOutliers.length === 0) {
        return null;
    }

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column'
        }}>
            <Typography variant="h2" sx={{
                fontSize: '24px',
                padding: '16px 0'
            }}>
                {Translate('economy/photographer-invoice-collection/pending/outliers/title')}
                <Tooltip
                    title={Translate(
                        'economy/photographer-invoice-collection/pending/outliers/description'
                    )}
                    placement="top-start"
                >
                    <InfoIcon />
                </Tooltip>
            </Typography>
            {displayedOutliers.map((outlier, index) => (
                <OutlierRow
                    key={outlier.orderRowId}
                    index={index}
                    outlier={outlier}
                    onClickMarkAsReviewed={handleMarkAsReviewed}
                />
            ))}
        </Box>
    );
}
